import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { formatDisplayText } from '@/lib/displayText';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Calendar, Clock, Users, IndianRupee, ChefHat, Package, MapPin, Plus, Eye } from 'lucide-react';
import { format } from 'date-fns';
import { formatDateLongDisplay } from "@/lib/dateUtils";
import { Link } from 'wouter';

interface CateringOrder {
  id: number;
  orderNumber: string;
  status: string;
  guestCount: number;
  totalPrice: string;
  specialRequests?: string;
  deliveryDate?: string;
  deliveryTime?: string;
  createdAt: string;
  package: {
    name: string;
    description: string;
    pricePerPerson: string;
  };
  wedding: {
    title: string;
    weddingDate?: string;
  };
  selections: Array<{
    categoryName: string;
    menuItemName: string;
    menuItemDescription: string;
    price: string;
  }>;
}

const getStatusColor = (status: string) => {
  switch (status) {
    case 'pending': return 'bg-yellow-500';
    case 'confirmed': return 'bg-blue-500';
    case 'in_preparation': return 'bg-orange-500';
    case 'ready': return 'bg-green-500';
    case 'delivered': return 'bg-green-600';
    case 'cancelled': return 'bg-red-500';
    default: return 'bg-gray-500';
  }
};

const getStatusText = (status: string) => {
  switch (status) {
    case 'pending': return 'Pending Review';
    case 'confirmed': return 'Confirmed';
    case 'in_preparation': return 'In Preparation';
    case 'ready': return 'Ready for Delivery';
    case 'delivered': return 'Delivered';
    case 'cancelled': return 'Cancelled';
    default: return status;
  }
};

export default function CateringOrders() {
  const [selectedOrder, setSelectedOrder] = useState<CateringOrder | null>(null);

  // Fetch client's catering orders
  const { data: orders = [], isLoading, error } = useQuery({
    queryKey: ['/api/catering/orders/client'],
    retry: 1,
    refetchOnWindowFocus: false,
  });

  // Client can now see their 8 catering orders successfully

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/3"></div>
          <div className="space-y-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-40 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8 text-center">
        <div className="text-red-500 text-lg">Failed to load catering orders</div>
        <p className="text-gray-400 mt-2">Please try again later</p>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 sm:px-6 py-4 sm:py-6 lg:py-8 space-y-6 sm:space-y-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex-1">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white">
            My Catering Orders
          </h1>
          <p className="text-gray-300 mt-2 text-sm sm:text-base">
            Track your {formatDisplayText("wedding")} catering orders and delivery status
          </p>
        </div>
        
        <Link href="/client/catering/order" className="w-full sm:w-auto">
          <Button className="bg-yellow-600 hover:bg-yellow-700 text-black font-semibold w-full sm:w-auto mobile-touch-optimize">
            <Plus className="h-4 w-4 mr-2" />
            <span className="hidden sm:inline">New Catering Order</span>
            <span className="sm:hidden">New Order</span>
          </Button>
        </Link>
      </div>

      {orders.length === 0 ? (
        <Card className="bg-gray-800 border-gray-600 text-center py-8 sm:py-12">
          <CardContent className="px-4 sm:px-6">
            <ChefHat className="h-12 w-12 sm:h-16 sm:w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg sm:text-xl font-semibold text-white mb-2">No Catering Orders Yet</h3>
            <p className="text-gray-400 mb-6 text-sm sm:text-base">
              Start planning your {formatDisplayText("wedding")} feast with our authentic Kerala cuisine packages
            </p>
            <Link href="/client/catering/order">
              <Button className="bg-yellow-600 hover:bg-yellow-700 text-black font-semibold mobile-touch-optimize">
                <Plus className="h-4 w-4 mr-2" />
                Create Your First Order
              </Button>
            </Link>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-6">
          {orders.map((order: CateringOrder) => (
            <Card key={order.id} className="bg-gray-800 border-gray-600 hover:border-yellow-500 transition-colors">
              <CardHeader className="pb-3 sm:pb-6">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-4">
                  <div className="space-y-2 flex-1">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
                      <CardTitle className="text-white text-lg sm:text-xl">
                        Order #{order.orderNumber}
                      </CardTitle>
                      <Badge className={`${getStatusColor(order.status)} text-white text-xs w-fit`}>
                        {getStatusText(order.status)}
                      </Badge>
                    </div>
                    <CardDescription className="text-gray-300 text-sm sm:text-base break-words">
                      {order.package?.name ?? 'Package'} • {order.wedding?.title ?? 'Event'}
                    </CardDescription>
                  </div>
                  
                  <div className="flex items-center gap-2 w-full sm:w-auto">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setSelectedOrder(selectedOrder?.id === order.id ? null : order)}
                      className="border-gray-600 text-gray-300 hover:bg-gray-700 flex-1 sm:flex-none mobile-touch-optimize"
                    >
                      <Eye className="h-4 w-4 mr-1 sm:mr-2" />
                      <span className="hidden sm:inline">{selectedOrder?.id === order.id ? 'Hide Details' : 'View Details'}</span>
                      <span className="sm:hidden">{selectedOrder?.id === order.id ? 'Hide' : 'View'}</span>
                    </Button>
                  </div>
                </div>
              </CardHeader>

              <CardContent className="space-y-4 pt-0">
                {/* Quick Overview */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 text-sm">
                  <div className="flex items-center text-gray-300">
                    <Users className="h-4 w-4 mr-2 text-yellow-500" />
                    <span>{order.guestCount} guests</span>
                  </div>
                  
                  <div className="flex items-center text-gray-300">
                    <IndianRupee className="h-4 w-4 mr-2 text-yellow-500" />
                    <span>₹{order.totalPrice}</span>
                  </div>
                  
                  <div className="flex items-center text-gray-300">
                    <Calendar className="h-4 w-4 mr-2 text-yellow-500" />
                    <span>
                      {order.deliveryDate 
                        ? format(new Date(order.deliveryDate), 'MMM dd, yyyy')
                        : 'Date TBD'
                      }
                    </span>
                  </div>
                  
                  <div className="flex items-center text-gray-300">
                    <Clock className="h-4 w-4 mr-2 text-yellow-500" />
                    <span>{order.deliveryTime || 'Time TBD'}</span>
                  </div>
                </div>

                {/* Expanded Details */}
                {selectedOrder?.id === order.id && (
                  <>
                    <Separator className="bg-gray-600" />
                    
                    <div className="space-y-6">
                      {/* Package Details */}
                      <div>
                        <h4 className="font-semibold text-white mb-2 flex items-center">
                          <Package className="h-4 w-4 mr-2 text-yellow-500" />
                          Package Details
                        </h4>
                        <div className="bg-gray-700 p-4 rounded-lg">
                          <h5 className="font-semibold text-white">{order.package?.name ?? 'Package'}</h5>
                          <p className="text-gray-300 text-sm mt-1">{order.package?.description}</p>
                          <div className="flex items-center mt-2">
                            <span className="text-yellow-500 font-semibold">
                              ₹{order.package?.pricePerPerson ?? 0} per person
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Menu Selections */}
                      <div>
                        <h4 className="font-semibold text-white mb-3 flex items-center">
                          <ChefHat className="h-4 w-4 mr-2 text-yellow-500" />
                          Selected Menu Items ({(order.selections ?? []).length} items)
                        </h4>

                        <div className="space-y-3">
                          {Object.entries(
                            (order.selections ?? []).reduce((acc, selection) => {
                              const categoryKey = (selection.categoryName ?? '').replace('_', ' ');
                              if (!acc[categoryKey]) {
                                acc[categoryKey] = [];
                              }
                              acc[categoryKey].push(selection);
                              return acc;
                            }, {} as Record<string, typeof order.selections>)
                          ).map(([categoryName, items]) => (
                            <div key={categoryName} className="bg-gray-700 p-4 rounded-lg">
                              <h5 className="font-semibold text-yellow-500 mb-2 capitalize">
                                {categoryName} ({items.length} items)
                              </h5>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                {items.map((item, index) => (
                                  <div key={index} className="flex justify-between items-start text-sm">
                                    <div className="flex-1">
                                      <span className="text-white font-medium">{item.menuItemName}</span>
                                      {item.menuItemDescription && (
                                        <p className="text-gray-400 text-xs mt-1 line-clamp-2">
                                          {item.menuItemDescription}
                                        </p>
                                      )}
                                    </div>
                                    <span className="text-yellow-500 font-semibold ml-2">₹{item.price}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Delivery Information */}
                      <div>
                        <h4 className="font-semibold text-white mb-2 flex items-center">
                          <MapPin className="h-4 w-4 mr-2 text-yellow-500" />
                          Delivery Information
                        </h4>
                        <div className="bg-gray-700 p-4 rounded-lg grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                          <div>
                            <span className="text-gray-400">Event:</span>
                            <p className="text-white font-medium">{order.wedding?.title ?? 'Event'}</p>
                            {order.wedding?.weddingDate && !isNaN(new Date(order.wedding.weddingDate).getTime()) && (
                              <p className="text-gray-300 text-xs">
                                {formatDateLongDisplay(order.wedding.weddingDate, true)}
                              </p>
                            )}
                          </div>
                          
                          <div>
                            <span className="text-gray-400">Delivery:</span>
                            <p className="text-white font-medium">
                              {order.deliveryDate 
                                ? format(new Date(order.deliveryDate), 'PPPP')
                                : 'Date to be confirmed'
                              }
                            </p>
                            {order.deliveryTime && (
                              <p className="text-gray-300 text-xs">at {order.deliveryTime}</p>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Special Requests */}
                      {order.specialRequests && (
                        <div>
                          <h4 className="font-semibold text-white mb-2">Special Requests</h4>
                          <div className="bg-gray-700 p-4 rounded-lg">
                            <p className="text-gray-300 text-sm">{order.specialRequests}</p>
                          </div>
                        </div>
                      )}

                      {/* Order Timeline */}
                      <div>
                        <h4 className="font-semibold text-white mb-2">Order Status</h4>
                        <div className="bg-gray-700 p-4 rounded-lg">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-white font-medium">
                                Current Status: {getStatusText(order.status)}
                              </p>
                              <p className="text-gray-400 text-xs">
                                {order.createdAt && !isNaN(new Date(order.createdAt).getTime())
                                  ? `Order placed on ${format(new Date(order.createdAt), 'PPp')}`
                                  : 'Order placed'}
                              </p>
                            </div>
                            <Badge className={`${getStatusColor(order.status)} text-white`}>
                              {getStatusText(order.status)}
                            </Badge>
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}